import { useCallback, useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { LEGAL_VERSIONS, type DocumentType } from '@/legal/versions';

export type ConsentStatus = 'loading' | 'ok' | 'required';

/**
 * Checks whether the logged-in user has accepted the current version of both
 * legal documents. Returns `'required'` when at least one acceptance is missing,
 * `'ok'` when everything is up-to-date, and `'loading'` while the check is in
 * flight.
 *
 * `acceptAll()` inserts the missing consents and transitions the status to `'ok'`.
 */
export function useLegalConsent(user: User | null): {
  status: ConsentStatus;
  acceptAll: () => Promise<void>;
} {
  const [status, setStatus] = useState<ConsentStatus>('loading');

  const checkConsents = useCallback(async (userId: string) => {
    if (!supabase) {
      setStatus('ok');
      return;
    }

    const types: DocumentType[] = ['terms', 'privacy'];
    const needed: { document_type: DocumentType; version: string }[] = [];

    for (const type of types) {
      const requiredVersion = LEGAL_VERSIONS[type];
      const { data } = await supabase
        .from('user_legal_consents')
        .select('id')
        .eq('user_id', userId)
        .eq('document_type', type)
        .eq('version', requiredVersion)
        .maybeSingle();

      if (!data) {
        needed.push({ document_type: type, version: requiredVersion });
      }
    }

    setStatus(needed.length > 0 ? 'required' : 'ok');
  }, []);

  useEffect(() => {
    if (!user) {
      setStatus('loading');
      return;
    }
    void checkConsents(user.id);
  }, [user, checkConsents]);

  const acceptAll = useCallback(async () => {
    if (!supabase || !user) return;

    // Collect forensic metadata for the audit trail.
    const userAgent = navigator.userAgent;
    let ipAddress: string | null = null;
    try {
      const res = await fetch('https://api.ipify.org?format=json');
      if (res.ok) {
        const json = (await res.json()) as { ip?: string };
        ipAddress = json.ip ?? null;
      }
    } catch {
      // IP lookup is best-effort – consent is stored even without it.
    }

    const types: DocumentType[] = ['terms', 'privacy'];
    const rows = types.map((type) => ({
      user_id: user.id,
      document_type: type,
      version: LEGAL_VERSIONS[type],
      ip_address: ipAddress,
      user_agent: userAgent,
    }));

    await supabase.from('user_legal_consents').upsert(rows, {
      onConflict: 'user_id,document_type,version',
      ignoreDuplicates: true,
    });

    setStatus('ok');
  }, [user]);

  return { status, acceptAll };
}
